import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePrevious } from 'react-use';

import { unselectItemAction } from '@air/redux-selected-items';

import { useWorkspaceAgentExpandedAssetView } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentExpandedAssetView';
import { useUploadedBatchOfferStore } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/useUploadedBatchOfferStore';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { useFrozenWhile } from '~/hooks/useFrozenWhile';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { centralizedClipSelector } from '~/store/centralizedClip/selectors';
import { selectedClipItemsSelector } from '~/store/selectedItems/selectors';

export type WorkspaceAgentComposerAssetPill = {
  id: string;
  type: 'asset';
  assetId: string;
  versionId: string;
  name: string;
  thumbnailUrl: string | null;
};

export type WorkspaceAgentComposerBoardPill = {
  type: 'board';
  boardId: string;
  name: string;
};

export type WorkspaceAgentComposerScopePill = WorkspaceAgentComposerAssetPill | WorkspaceAgentComposerBoardPill;

export type PrivateWorkspaceAgentComposerPillsApi = {
  boardContextPill: WorkspaceAgentComposerBoardPill | null;
  hasExplicitSelection: boolean;
  scopePills: WorkspaceAgentComposerScopePill[];
  clearScope: () => void;
};

const EMPTY_SCOPE_PILLS: WorkspaceAgentComposerScopePill[] = [];

export const usePrivateWorkspaceAgentComposerPills = (): PrivateWorkspaceAgentComposerPillsApi => {
  const dispatch = useDispatch();
  const selectedClipItems = useSelector(selectedClipItemsSelector);
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const boardId = useURLBoardIdSelector();
  const centralizedBoard = useSelector(centralizedBoardSelector);
  const centralizedClip = useSelector(centralizedClipSelector);
  const isComposerAssetViewScopeOptedOut = useWorkspaceAgentPanelStore(
    (state) => state.isComposerAssetViewScopeOptedOut,
  );
  const resetComposerBoardScopeOptOut = useWorkspaceAgentPanelStore((state) => state.resetComposerBoardScopeOptOut);
  const optOutComposerAssetViewScope = useWorkspaceAgentPanelStore((state) => state.optOutComposerAssetViewScope);
  const { clipId, isExpandedAssetViewComposerActive, isExpandedAssetViewModalSession } =
    useWorkspaceAgentExpandedAssetView();

  const previousClipId = usePrevious(clipId);
  const uploadedBatchAttachments = useUploadedBatchOfferStore((state) => state.attachments);

  const assetPills = useMemo<WorkspaceAgentComposerAssetPill[]>(() => {
    // A real clip selection or select-all replaces the upload batch. The batch is composer scope, not a selection.
    if (selectedClipItems.length === 0 && !selectAll && uploadedBatchAttachments.length > 0) {
      return uploadedBatchAttachments.map(
        (attachment) =>
          ({
            id: attachment.id,
            type: 'asset',
            assetId: attachment.assetId,
            versionId: attachment.versionId,
            name: attachment.name,
            thumbnailUrl: attachment.thumbnailUrl,
          }) satisfies WorkspaceAgentComposerAssetPill,
      );
    }

    return selectedClipItems.map((selected) => {
      const clip = selected.item;

      return {
        // The selection's own key rather than the asset's, so one selected item is always one pill
        // and one `@[pill:…]` mention. An asset is the coarser identity: nothing about the selection
        // stops two of its versions from being in it at once.
        id: selected.id,
        type: 'asset',
        assetId: clip.assetId,
        versionId: clip.id,
        name: clip.title || clip.importedName || 'Untitled',
        thumbnailUrl: clip.assets.image || null,
      } satisfies WorkspaceAgentComposerAssetPill;
    });
  }, [selectAll, selectedClipItems, uploadedBatchAttachments]);

  const boardPill = useMemo((): WorkspaceAgentComposerBoardPill | null => {
    if (!boardId || centralizedBoard?.id !== boardId) {
      return null;
    }

    return {
      boardId,
      name: centralizedBoard.title || 'Untitled board',
      type: 'board',
    };
  }, [boardId, centralizedBoard?.id, centralizedBoard?.title]);

  const retainAssetViewComposerScope =
    isExpandedAssetViewModalSession && Boolean(clipId) && !isComposerAssetViewScopeOptedOut;

  const viewedClipMatchesUrl = Boolean(clipId) && centralizedClip?.id === clipId;

  const scopePillsBeforeFreeze = useMemo((): WorkspaceAgentComposerScopePill[] => {
    if (selectAll) {
      return EMPTY_SCOPE_PILLS;
    }

    if (assetPills.length > 0) {
      return assetPills;
    }

    if (retainAssetViewComposerScope && viewedClipMatchesUrl && centralizedClip) {
      return [
        {
          id: centralizedClip.id,
          type: 'asset',
          assetId: centralizedClip.assetId,
          versionId: centralizedClip.id,
          name: centralizedClip.title || centralizedClip.importedName || 'Untitled',
          thumbnailUrl: centralizedClip.assets.image || null,
        },
      ];
    }

    if (retainAssetViewComposerScope) {
      return EMPTY_SCOPE_PILLS;
    }

    if (boardPill) {
      return [boardPill];
    }

    return EMPTY_SCOPE_PILLS;
  }, [assetPills, boardPill, centralizedClip, retainAssetViewComposerScope, selectAll, viewedClipMatchesUrl]);

  const isInModalClipTransition =
    retainAssetViewComposerScope && centralizedClip?.id !== clipId && previousClipId !== undefined;

  const { value: scopePills } = useFrozenWhile(scopePillsBeforeFreeze, isInModalClipTransition);

  const boardContextPill = useMemo((): WorkspaceAgentComposerBoardPill | null => {
    if (selectAll) {
      return null;
    }

    return boardPill;
  }, [boardPill, selectAll]);

  // WORK-373: board select-all is represented in the agent selection bar only — not the composer
  // scope chip or send metadata. WORK-656: while the asset modal is open, the viewed clip counts as
  // explicit scope even before Redux selection catches up (!selectAll).
  const hasExplicitSelection = scopePills.length > 0 && scopePills.every((pill) => pill.type === 'asset') && !selectAll;

  const previousAssetPillCountRef = useRef(assetPills.length);

  useEffect(() => {
    if (!boardId) {
      return;
    }

    resetComposerBoardScopeOptOut();
  }, [boardId, resetComposerBoardScopeOptOut]);

  useEffect(() => {
    const previousCount = previousAssetPillCountRef.current;
    previousAssetPillCountRef.current = assetPills.length;

    if (previousCount === 0 && assetPills.length > 0) {
      resetComposerBoardScopeOptOut();
    }
  }, [assetPills.length, resetComposerBoardScopeOptOut]);

  const clearScope = useCallback(() => {
    for (const pill of assetPills) {
      dispatch(unselectItemAction({ itemId: pill.id }));
    }

    if (isExpandedAssetViewComposerActive) {
      optOutComposerAssetViewScope();
    }

    useUploadedBatchOfferStore.getState().clearAttachedBatch();
  }, [assetPills, dispatch, isExpandedAssetViewComposerActive, optOutComposerAssetViewScope]);

  return useMemo(
    () => ({ boardContextPill, clearScope, hasExplicitSelection, scopePills }),
    [boardContextPill, clearScope, hasExplicitSelection, scopePills],
  );
};
